import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Filter, Star, Plus, ExternalLink, ThumbsUp, ThumbsDown, ArrowRight } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { AdminBar } from '../components/AdminBar';
import { useTitle } from '../hooks/useTitle';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

interface AiTool {
  id: string;
  name: string;
  description: string;
  category: string;
  pricing_type: string;
  pricing_details: string;
  features: string[];
  use_cases: string[];
  pros: string[];
  cons: string[];
  website_url: string;
  image_url: string;
  rating: number;
  reviews_count: number;
}

export function AiTools() {
  const { setTitle } = useTitle();
  const [tools, setTools] = useState<AiTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setTitle('Outils IA - Novster AI');
    fetchTools();
  }, [setTitle]);

  const fetchTools = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_tools')
        .select('*')
        .order('rating', { ascending: false });

      if (error) throw error;
      setTools(data || []);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = Array.from(new Set(tools.map(tool => tool.category)));

  const filteredTools = tools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const renderStars = (rating: number) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < Math.floor(rating)
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-6 animate-slideUp">
            Les Meilleurs Outils IA
          </h1>
          <p className="text-xl text-center text-gray-600 max-w-2xl mx-auto mb-12 animate-slideUp">
            Une sélection des outils d'intelligence artificielle les plus performants et innovants
          </p>

          {/* Search and Filter */}
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-4 mb-12">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher un outil..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-8 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-white"
              >
                <option value="">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center text-gray-600">Chargement des outils...</div>
          ) : filteredTools.length === 0 ? (
            <div className="text-center text-gray-600">Aucun outil trouvé</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTools.map((tool, index) => (
                <article
                  key={tool.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all animate-slideUp"
                  style={{ '--delay': `${0.1 * index}s` } as React.CSSProperties}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={tool.image_url}
                      alt={tool.name}
                      className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                        {tool.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold">{tool.name}</h2>
                      <a
                        href={tool.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:text-orange-600"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {tool.description}
                    </p>
                    <div className="flex items-center gap-2 mb-4">
                      {renderStars(tool.rating)}
                      <span className="text-sm text-gray-500">
                        ({tool.reviews_count} avis)
                      </span>
                    </div>
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Prix: {tool.pricing_type}
                      </div>
                      <div className="text-sm text-gray-600">
                        {tool.pricing_details}
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                          Points forts
                          <Link
                            to={`/tools/${tool.id}`}
                            className="text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1 group"
                          >
                            Voir plus
                            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </h3>
                        <ul className="space-y-2">
                          {tool.pros.slice(0, 3).map((pro, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm">
                              <ThumbsUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">
                          Points d'attention
                        </h3>
                        <ul className="space-y-2">
                          {tool.cons.slice(0, 2).map((con, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm">
                              <ThumbsDown className="w-4 h-4 text-red-500 flex-shrink-0 mt-1" />
                              <span>{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {isAuthenticated && (
            <div className="mt-12 text-center">
              <button
                onClick={() => navigate('/admin/tools/new')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2"
              >
                Ajouter un outil
                <Plus className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}